import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Probe /auth/me on every mount: either the httpOnly cookie or the legacy
  // Authorization header (Bearer <localStorage token>) will authenticate us.
  // If neither succeeds we silently treat it as a logged-out session.
  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        // Clean up a stale legacy token so we stop sending it.
        if (localStorage.getItem("md_token")) {
          localStorage.removeItem("md_token");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // Cookie is set by the server; we no longer need the token in localStorage.
    // Clear any legacy token so future axios requests rely on the cookie only.
    localStorage.removeItem("md_token");
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (name, email, password) => {
    const r = await api.post("/auth/register", { name, email, password });
    localStorage.removeItem("md_token");
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Network errors are non-fatal, we still clear local state.
      console.warn("[Auth] logout request failed:", e?.message || e);
    }
    localStorage.removeItem("md_token");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (e) {
      console.warn("[Auth] /auth/me refresh failed:", e?.message || e);
      return null;
    }
  };

  const ctxValue = useMemo(
    () => ({ user, loading, login, register, logout, refresh }),
    [user, loading]
  );
  return <AuthCtx.Provider value={ctxValue}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
